import React from 'react';
import styled from 'styled-components/macro';
import Select from 'react-select';
import { SmsOptions } from 'lib/const';
import { useSmsUserContext } from 'lib/context';
import { theme } from 'lib/style';
import {
  MainContainer,
  Search,
  Table,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
  TableContextProvider,
  PrimaryButton,
} from 'lib/components';
import { ReassignChatModal } from './ReassignChatModal';

const FiltersContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${theme.mediaQueryMinWidth.sm} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Filter = styled.div`
  float: right;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${theme.mediaQueryMinWidth.sm} {
    width: 250px;
  }
`;

const TabsContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 16px;
  align-items: center;
  ${theme.mediaQueryMinWidth.mb} {
    padding: 0;
    nav {
      &:nth-of-type(1) {
        display: flex;
      }
      width: 100%;
      height: 48px;
      > div {
        float: left;
        display: block;
        background-color: ${theme.palette.white};
        border-top: 1px solid ${theme.palette.grayBorder};
        border-left: 1px solid ${theme.palette.grayBorder};
        border-right: 1px solid ${theme.palette.grayBorder};
        height: 48px;
        box-sizing: border-box;
        line-height: 1.5;
        padding-top: 12px;
        &:nth-of-type(1) {
          width: 152px;
        }
        &:nth-of-type(2) {
          width: 115px;
          border-left: none;
          border-right: none;
        }
        &:nth-of-type(3) {
          width: 100px;
        }
        &:nth-of-type(4) {
          flex-grow: 2;
          border-left: none;
          border-right: none;
          border-top: none;
          border-bottom: 1px solid ${theme.palette.grayBorder};
          background-color: #fbfbfb;
          &:hover {
            cursor: default;
          }
          disabled: true;
        }
      }
    }
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${theme.mediaQueryMaxWidth.md} {
    flex-direction: column;
  }
`;

const Layout = styled.div`
  ${theme.fontNormal500};
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const TableCell = styled.div<{ width: number }>`
  width: ${props => (props.width ? props.width + 'px' : 'auto')};
  padding-left: 24px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const DateLabel = styled.span`
  color: #9297a2;
  font-size: 14px;
`;

const tableFields = [
  {
    value: 'name',
    label: 'Name + Number',
  },
  {
    value: 'lastResponse',
    label: 'Last Response',
  },
  {
    value: 'sent',
    label: 'Last Owner',
  },
];

export const ArchiveLayout = () => {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(10);
  const [showReassignModal, setShowReassignModal] = React.useState(false);

  const {
    state: {
      messages: { items },
    },
  } = useSmsUserContext();
  const columnWidths = [200, 350];

  const onSearch = (query: string) => {
    setSearch(query.toLowerCase());
    setPage(0);
  };

  const onPaginationChange = ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setSize(newSize);
    setPage(newSize !== size ? 0 : newPage);
  };

  return (
    <>
      <MainContainer>
        <TabsContainer>
          <FiltersContainer>
            <Filter>
              <h3>Messages Archive</h3>
            </Filter>
          </FiltersContainer>
        </TabsContainer>
        <Content>
          <Layout>
            <Row style={{ marginBottom: 20 }}>
              <div style={{ width: 300 }}>
                <Search
                  prevSearch={search}
                  placeholder="Search by number or name..."
                  onSearch={onSearch}
                />
              </div>
              <Row>
                <Select
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      height: '40px',
                      width: '200px',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                  options={SmsOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement={'bottom'}
                  placeholder={'All users'}
                  value={SmsOptions.find(o => {
                    return o.value === '7d';
                  })}
                  onChange={() => {}}
                />
              </Row>
            </Row>
            <TableContextProvider
              total={100}
              onChange={onPaginationChange}
              initPage={page}
              initSize={size}
            >
              <div style={{ overflowX: 'auto' }}>
                <Table
                  compact={true}
                  headers={[
                    ...tableFields.map((item, index) => {
                      return (
                        <TableCell
                          width={columnWidths[index]}
                          onClick={() => {}}
                        >
                          {item.label}
                        </TableCell>
                      );
                    }),
                    <TableCell width={columnWidths[tableFields.length]} />,
                  ]}
                  hoverable={false}
                  columnWidths={columnWidths}
                  rows={items.map((contact: any, index: number) => ({
                    key: index,
                    columns: [
                      ...tableFields.map((item, index) => {
                        return (
                          <TableCell
                            width={columnWidths[index]}
                            onClick={() => {}}
                          >
                            {
                              // @ts-ignore
                              contact[item.value]
                            }
                            <br />
                            {// @ts-ignore
                            item.value == 'lastResponse' ? (
                              <DateLabel>2020-04-17 01:23 PM</DateLabel>
                            ) : null}
                          </TableCell>
                        );
                      }),
                      <TableCell
                        style={{ textAlign: 'right', justifyContent: 'end' }}
                        width={columnWidths[tableFields.length]}
                      >
                        <PrimaryButton
                          text={'Restore'}
                          fontSize="15px"
                          textColor={theme.palette.black_1_100}
                          backgroundColor={
                            theme.palette.primaryDarkBlue10Percent
                          }
                          height="40px"
                          borderRadius={5}
                          margin="0px 12px 0px 12px"
                          onClick={() => {
                            setShowReassignModal(true);
                          }}
                        />
                      </TableCell>,
                    ],
                    onClick: () => {},
                  }))}
                />
              </div>
              {items.length === 0 && (
                <p
                  style={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    margin: '36px 0',
                  }}
                >
                  No matching results found.{' '}
                </p>
              )}

              <TableFooter>
                <TablePaginationNew />
                <TablePaginationSizeNew globalName="sms_user_list" />
              </TableFooter>
            </TableContextProvider>
          </Layout>
        </Content>
        {showReassignModal && (
          <ReassignChatModal
            handleModalClose={() => {
              setShowReassignModal(false);
            }}
            title="Restore Archived Chat"
            buttonLabel="Restore"
            selectLabel="Assigned Chat Owner"
          />
        )}
      </MainContainer>
    </>
  );
};
